import { v5 as uuidv5 } from 'uuid';
import Logger from '../Logger/Logger';
import { ConfigService } from '../Configs/ConfigService';
import { LogLabels } from '../Logger/LogLabels';
import { ApiConfigService } from './ApiConfig';
import { CacheHandler } from './CacheHandler';
import { rawRequest } from './RawRequest';
import { Messages } from './types/message';
import {
  HTTPVerbs,
  RequestOptions,
  GetRequestOptions,
  PostRequestOptions,
  PutRequestOptions,
  DeleteRequestOptions,
  PatchRequestOptions,
  HttpAugments,
} from './types/httpTypes';
import { generateRandomHex, uniqueId } from './UniqIdUtils';
import { API_CONSTANTS } from './ApiConstants';

const fallbackIdempotancyKey = uniqueId();
let controller: AbortController;
/**
 *
 * @param requestOptions takes all request parameters
 * @param augments can be passed to configure request/response.
 * @returns Promise
 */

const request = <T>(requestOptions: RequestOptions, augments?: HttpAugments): Promise<T> =>
  new Promise((resolve, reject) => {
    const loggerServiceInstance = Logger.getInstance();
    loggerServiceInstance.debug('Step-1', LogLabels.RequestStart, requestOptions);

    controller = new AbortController();
    const config = ConfigService.getConfigs();
    const baseUrl = ApiConfigService.getBaseUrl();
    const cacheInstance = CacheHandler.getInstance();

    const customCache = augments?.customCache || false;
    const { url, timeout = (config.NEXT_PUBLIC_REQUEST_TIMEOUT as number) || 6000 } = requestOptions;
    const _options: RequestOptions = augmentRequest(requestOptions, augments);

    // handle cache and prevent duplicate req
    if (customCache) {
      if (cacheInstance.hasUrl(url)) {
        loggerServiceInstance.debug('Step-6', LogLabels.RequestUrlAlreadyRequested(url), requestOptions, 'color: red');
        resolve({ res: Messages.error.E005 } as T);
        return;
      } else {
        cacheInstance.pushUrl(url);
        loggerServiceInstance.debug('Step-6', LogLabels.RequestUrlCached, requestOptions);
      }
    }

    // Abort req if time taken is more the timeout
    const timeoutId = setTimeout(() => {
      loggerServiceInstance.error({
        message: Messages.error.E007,
        status: 504,
      });
      controller.abort();
      reject({ message: Messages.error.E007, status: 504 });
    }, timeout);

    // Api call using rawRequest Method
    rawRequest(url.indexOf('http://') === -1 && url.indexOf('https://') === -1 ? `${baseUrl}${url}` : url, _options)
      .then(async (response: Response) => {
        const _response = await augmentResponse(response, augments);
        loggerServiceInstance.debug('Step-9', LogLabels.RequestComplete, requestOptions);
        resolve(_response as T);
      })
      .catch((error) => {
        loggerServiceInstance.error(LogLabels.RequestError, error);
        reject(error);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        customCache && cacheInstance.remove(url);
      });
  });

request.get = <T>(options: GetRequestOptions, augments?: HttpAugments): Promise<T> =>
  request({ ...options, method: HTTPVerbs.GET }, augments);
request.post = <T>(options: PostRequestOptions, augments?: HttpAugments): Promise<T> =>
  request({ ...options, method: HTTPVerbs.POST }, augments);
request.put = <T>(options: PutRequestOptions, augments?: HttpAugments): Promise<T> =>
  request({ ...options, method: HTTPVerbs.PUT }, augments);
request.delete = <T>(options: DeleteRequestOptions, augments?: HttpAugments): Promise<T> =>
  request({ ...options, method: HTTPVerbs.DELETE }, augments);
request.patch = <T>(options: PatchRequestOptions, augments?: HttpAugments): Promise<T> =>
  request({ ...options, method: HTTPVerbs.PATCH }, augments);

/**
 * @description A utility function to ensure the provided object is non-null or undefined
 * @param obj - Optional object to check.
 * @returns The provided object if non-null and non-undefined; otherwise, an empty object.
 *
 */
const checkObjectValue = (obj?: Record<string, unknown>): object => obj || {};
/**
 * @description updateOptionWithInterceptor - Updates HTTP request options with interceptors if provided.
 * @param options - RequestOptions - The original HTTP request options.
 * @param augments - Optional HttpAugments - Augmentations for request processing.
 * @returns Updated HTTP request options after applying interceptors, if any.
 *
 */
const updateOptionWithInterceptor = (options: RequestOptions, augments?: HttpAugments) => {
  const loggerServiceInstance = Logger.getInstance();
  let _options = { ...options };
  if (!augments?.byPassInterceptors && augments?.interceptors) {
    for (const element of augments.interceptors) {
      _options = element?.requestInterceptor(_options);
    }
    loggerServiceInstance.debug('Step-3', LogLabels.RequestInterceptorExecuted, options);
  }
  return _options;
};
function augmentRequest(options: RequestOptions, augments?: HttpAugments): RequestOptions {
  const loggerServiceInstance = Logger.getInstance();
  let apiHeaders = {};
  const { signal } = controller;
  if (!augments?.skipDefaultContentType) {
    Object.assign(apiHeaders, {
      [API_CONSTANTS.contentType]: 'application/json',
    });
    loggerServiceInstance.debug('Step-2', LogLabels.DefaultContentType, options);
  }
  let _options = options;
  const tuples = ApiConfigService.getTuples();
  apiHeaders = {
    ...apiHeaders,
    ...checkObjectValue(tuples),
  };
  const configs = ConfigService.getConfigs();
  if ((typeof window === 'undefined' || !window) && !configs.NEXT_PUBLIC_OTEL_TRACING) {
    const reqId = uuidv5(_options.url + uniqueId(), uuidv5.URL);
    const traceparent = `00-${generateRandomHex(32)}-${generateRandomHex(16)}-01`;
    apiHeaders = {
      ...apiHeaders,
      'X-Request-Id': reqId,
      //# traceparent value : {version}-{trace_id}-{span_id}-{trace_flags}
      traceparent: traceparent,
    };
    loggerServiceInstance.debug(
      LogLabels.ReqeuestWithTraceId,
      `reqId: ${reqId}`,
      `traceparent: ${traceparent}`,
      options,
    );
  }
  _options.headers = {
    ...apiHeaders,
    ...checkObjectValue(_options.headers),
  };

  //Request Interceptors added
  _options = updateOptionWithInterceptor(_options, augments);

  if (_options.body && _options.headers?.[API_CONSTANTS.contentType] === 'application/json') {
    _options.body = JSON.stringify(_options.body);
    loggerServiceInstance.debug('Step-4', LogLabels.RequestBodyParsed, _options);
  }
  if (augments?.log) {
    loggerServiceInstance.log(LogLabels.RequestOptions(_options), 'color: blue');
  }

  if (_options.method === HTTPVerbs.POST) {
    if (!_options.body) {
      loggerServiceInstance.warn(Messages.error.E003);
    }
  }
  /**
   * set idempotancy id for post, patch, put, delete request
   */
  if (
    (_options.method === HTTPVerbs.POST || _options.method === HTTPVerbs.PATCH || _options.method === HTTPVerbs.PUT) &&
    !augments?.skipIdempotency
  ) {
    _options.headers = { ..._options.headers };
    if (augments?.customKeyGenerator) {
      const idempotancyKey = augments.customKeyGenerator();
      _options.headers['X-Idempotency-Key'] = idempotancyKey;
    } else {
      const uuid = uuidv5(_options.url + fallbackIdempotancyKey, uuidv5.URL);
      _options.headers['X-Idempotency-Key'] = uuid;
    }
    loggerServiceInstance.debug('Step-5', LogLabels.RequestIdempotencyAdded, _options);
  }
  return {
    ..._options,
    customRetryFunction: _options.customRetryFunction,
    signal,
  };
}
async function augmentResponse(response: Response, augments?: HttpAugments) {
  const loggerServiceInstance = Logger.getInstance();
  let _response = response;
  try {
    const contentTypeHeader = response.headers.get(API_CONSTANTS.contentType);
    if (contentTypeHeader?.includes('json')) {
      _response = await response.json();
      loggerServiceInstance.debug('Step-7', LogLabels.ResponseConvertedToJSON, response);
    }
    if (augments && !augments.byPassInterceptors && augments.interceptors) {
      for (let i = augments.interceptors.length - 1; i >= 0; i--) {
        _response = augments.interceptors[i]?.responseInterceptor(_response);
      }
      loggerServiceInstance.debug('Step-8', LogLabels.ResponseInterceptorExecuted, response);
    }
    return _response;
  } catch (err) {
    loggerServiceInstance.error(err);
    throw err;
  }
}
export { request };
